import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  CalendarEvent,
  Search,
  FileEarmarkExcelFill
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx"; 
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_AUDIT_LOGS = `${API_BASE}/getAuditLogs`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  return { displayName, userId };
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTimeOnly(dtStr) {
  if (!dtStr) return "-";
  const d = new Date(dtStr);
  if (Number.isNaN(+d)) return "";
  return d.toLocaleTimeString("en-SG", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const ACCOUNT_TYPE_STYLES = {
  admin: {
    bg: "#FEF2F2",
    color: "#B91C1C",
    border: "1px solid #FECACA",
  },
  staff: {
    bg: "#EFF6FF",
    color: "#1D4ED8",
    border: "1px solid #BFDBFE",
  },
  stbadmin: {
    bg: "#F5F3FF",
    color: "#7C3AED",
    border: "1px solid #DDD6FE",
  },
  applicant: {
    bg: "#ECFDF3",
    color: "#15803D",
    border: "1px solid #BBF7D0",
  },
  default: {
    bg: "#F3F4F6",
    color: "#374151",
    border: "1px solid rgba(0,0,0,0.06)",
  },
};

function formatAccountTypeLabel(value) {
  if (!value) return "User";
  const v = value.toLowerCase();
  if (v === "stbadmin") return "STB Admin";
  if (v === "admin") return "Admin";
  if (v === "staff") return "Staff";
  if (v === "applicant") return "Applicant";
  return value;
}

export default function AuditLogs() {
  const { displayName } = useMemo(getStoredUser, []);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const [dateFrom, setDateFrom] = useState(() => {
    const t = new Date();
    const d = new Date(t);
    d.setDate(t.getDate() - 6);
    return toISODate(d);
  });

  const [dateTo, setDateTo] = useState(() => {
    const t = new Date();
    return toISODate(t);
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      if (!API_BASE) {
        setRows([]);
        setLoading(false);
        return;
      }

      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const { data } = await axios.get(ENDPOINT_AUDIT_LOGS, {
        params: {
          dateFrom,
          dateTo,
        },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data?.data)) {
        setRows(
          data.data.map((r, idx) => ({
            id: r.user_id ?? idx + 1,
            user_id: r.user_id ?? idx + 1,
            username: r.username ?? "-",
            first_name: r.first_name ?? "",
            last_name: r.last_name ?? "",
            phone_number: r.phone_number ?? "",
            badge_no: r.badge_no ?? "",
            tg_expiry_date: r.tg_expiry_date ?? "",
            activity_subj: r.activity_subj ?? "",
            activity_desc: r.activity_desc ?? "",
            activity_date: r.activity_date ?? "",
            account_type: r.account_type ?? "",
          }))
        );
      } else {
        setRows([]);
        setErr(data?.message || "Failed to load audit logs.");
      }
    } catch (e) {
      setRows([]);
      setErr("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const clearFilters = () => {
    const t = new Date();
    const d = new Date(t);
    d.setDate(t.getDate() - 6);
    setDateFrom(toISODate(d));
    setDateTo(toISODate(t));
    setSearch("");
    setPage(1);
  };

  const filteredRows = rows.filter((r) => {
    const needle = search.toLowerCase();
    if (!needle) return true;

    const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
    return (
      (r.username || "").toLowerCase().includes(needle) ||
      (r.activity_subj || "").toLowerCase().includes(needle) ||
      (r.activity_desc || "").toLowerCase().includes(needle) ||
      name.includes(needle) ||
      (r.account_type || "").toLowerCase().includes(needle)
    );
  });

  const totalPages = Math.ceil(filteredRows.length / pageSize) || 1;
  const paginatedRows = filteredRows.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

 const handleExportExcel = () => {
  if (!rows.length) return;

  const exportData = rows.map((r) => {
    const fullName = `${r.first_name || ""} ${r.last_name || ""}`.trim();
    return {
      Date: formatDate(r.activity_date),
      Time: formatTimeOnly(r.activity_date),
      Username: String(r.username || "").toLowerCase(),
      Name:
        fullName
          .toLowerCase()
          .replace(/(^|[\s\-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase()) || "-",
      "Account Type": formatAccountTypeLabel(r.account_type),
      Subject: r.activity_subj || "",
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Logs");

  const range = XLSX.utils.decode_range(worksheet['!ref']);

  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];

      if (!cell) continue;
      if (R === 0) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
        };
      } else {
        if (["A", "B", "E"].includes(cellAddress[0])) {
          cell.s = { alignment: { horizontal: "center" } };
        } else {
          cell.s = { alignment: { horizontal: "left" } };
        }
      }
    }
  }

  worksheet['!cols'] = [
    { wpx: 90 },  
    { wpx: 80 },  
    { wpx: 120 }, 
    { wpx: 150 }, 
    { wpx: 120 }, 
    { wpx: 220 }, 
  ];

  worksheet['!rows'] = Array.from({ length: range.e.r + 1 }, (_, i) => ({ hpt: i === 0 ? 30 : 24 }));

  const fileName = `audit_logs_${dateFrom}_to_${dateTo}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};


  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center justify-content-between"
            style={{ gap: 12 }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <span
                role="button"
                aria-label="Go back"
                tabIndex={0}
                onClick={handleBack}
                onKeyDown={(e) =>
                  e.key === "Enter" || e.key === " " ? handleBack() : null
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  background: "transparent",
                  cursor: "pointer",
                  outline: "none",
                }}
                className="back-icon"
              >
                <ArrowLeft size={18} color="#ffffff" />
              </span>
              <h4 className="m-0 fw-bold">AUDIT LOG</h4>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        <Row className="g-4">
          <Col xs={12}>
            <Card
              style={{
                background: PANEL_BG,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                borderRadius: 18,
              }}
            >
              <Card.Body style={{ background: "white", borderRadius: 16 }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <InputGroup style={{ maxWidth: 320 }}>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search username, name, subject or description..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </InputGroup>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* From date with gradient icon */}
                    <InputGroup style={{ width: 210 }}>
                      <InputGroup.Text
                        style={{
                          background:
                            "linear-gradient(135deg, #3D74F5, #9C6BFF)",
                          border: "none",
                          color: "#ffffff",
                        }}
                      >
                        <CalendarEvent size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        value={dateFrom}
                        onChange={(e) => {
                          setDateFrom(e.target.value || dateFrom);
                          setPage(1);
                        }}
                      />
                    </InputGroup>
                    <span className="text-muted small">to</span>
                    {/* To date with gradient icon */}
                    <InputGroup style={{ width: 210 }}>
                      <InputGroup.Text
                        style={{
                          background:
                            "linear-gradient(135deg, #3D74F5, #9C6BFF)",
                          border: "none",
                          color: "#ffffff",
                        }}
                      >
                        <CalendarEvent size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        value={dateTo}
                        onChange={(e) => {
                          setDateTo(e.target.value || dateTo);
                          setPage(1);
                        }}
                      />
                    </InputGroup>

                    <Button
                      variant="primary"
                      style={{ background: BRAND, borderColor: BRAND }}
                      onClick={load}
                    >
                      Apply
                    </Button>

                    <Button variant="outline-secondary" onClick={clearFilters}>
                      Clear
                    </Button>

                    <Button
                      variant="success"
                      onClick={handleExportExcel}
                      disabled={!rows.length}
                    >
                     <FileEarmarkExcelFill size={20}/>
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table hover align="start" className="mb-0">
                    <thead
                      style={{
                        background: PANEL_SURFACE,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <tr>
                        <th style={{ minWidth: 110 }}>Date</th>
                        <th style={{ minWidth: 90 }}>Time</th>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Account Type</th>
                        <th>Subject</th>
                        {/* <th>Description</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            <Spinner size="sm" className="me-2" /> Loading…
                          </td>
                        </tr>
                      ) : err ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center py-4 text-danger"
                          >
                            {err}
                          </td>
                        </tr>
                      ) : paginatedRows.length ? (
                        paginatedRows.map((r, idx) => {
                          const name =
                            `${r.first_name || ""} ${
                              r.last_name || ""
                            }`.trim() || "-";

                          const typeKey = (r.account_type || "").toLowerCase();
                          const style =
                            ACCOUNT_TYPE_STYLES[typeKey] ||
                            ACCOUNT_TYPE_STYLES.default;
                          const label = formatAccountTypeLabel(r.account_type);

                          return (
                            <tr key={`${r.id}-${r.activity_date}-${idx}`}>
                              <td className="col-date">
                                {formatDate(r.activity_date)}
                              </td>
                              <td className="col-time">
                                {formatTimeOnly(r.activity_date)}
                              </td>
                              <td className="col-user">{r.username}</td>
                              <td className="col-name">{name}</td>
                              <td className="col-type">
                                <span
                                  className="badge rounded-pill"
                                  style={{
                                    background: style.bg,
                                    color: style.color,
                                    border: style.border,
                                    fontWeight: 500,
                                    fontSize: "0.9rem",
                                    padding: "0.3rem 0.7rem",
                                  }}
                                >
                                  {label}
                                </span>
                              </td>
                              <td
                                style={{ maxWidth: 230 }}
                                className="col-subject"
                              >
                                <div
                                  className="text-truncate"
                                  title={r.activity_subj}
                                >
                                  {r.activity_subj || "-"}
                                </div>
                              </td>
                              {/* <td style={{ maxWidth: 320 }}>
                                <div
                                  className="text-truncate"
                                  title={r.activity_desc}
                                >
                                  {r.activity_desc || "-"}
                                </div>
                              </td> */}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            <div className="text-muted">
                              No audit logs found for the selected period.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <Form.Select
                      size="sm"
                      style={{ width: 70 }}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Form.Select>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <span className="small">
                      Page {page} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
